// Bagian PURE halaman Detail Produksi (/admin/orders/produksi/[id]) — tipe,
// agregasi, derivasi status, eligibility aksi, filter, dan pengelompokan varian.
//
// DIPISAH dari productionDetail.ts (yang ber-"use client") supaya bisa dipakai
// juga oleh route SERVER — ekspor runtime modul "use client" tak boleh diimpor
// route handler (jadi client reference, bukan fungsinya). Pemisahan ini cermin
// poDetail.ts ← board.ts yang dilakukan untuk /api/admin/po-reseller-export;
// di sini pemakainya /api/admin/production-reseller-export (Excel Rekap Per
// Pelanggan) yang WAJIB memakai agregator yang sama dengan layarnya supaya
// angka di file tak bisa menyimpang dari yang dilihat admin.
//
// JANGAN menambahkan import bergantung-browser (createBrowserSupabase dkk) ke
// file ini — fetcher & aksi RPC tetap tinggal di productionDetail.ts, yang
// me-re-export seluruh isi modul ini sehingga importer lama tak perlu berubah.

import { formatNotifAge } from "@/lib/adminNotifications";
import { permissionLabel, type AdminGrantKey } from "@/lib/adminPermissions";
import {
  addOrderItemPriceLine,
  smartVariantNameCompare,
  sortPriceLines,
  type PODetailPriceLine,
  type PriceLineSourceItem,
  type VariantNoteEntry
} from "@/lib/poDetail";
import {
  hasPriceAxis,
  variantPricingFromRow,
  type VariantPricing
} from "@/lib/backfillUnitPrice";
import { borrowedExtraAgrees, type VariantPriceSummary } from "@/lib/pricing";
import { sortReadyCandidates, type ReadyCandidate } from "@/lib/readyAllocation";

// ─── Tipe ─────────────────────────────────────────────────────────────────────

/**
 * Jenis biaya produksi internal (ledger production_costs, mig 079 + mig 4250).
 *
 * Lima nilai pertama = mode **belanja ke supplier**; semuanya TERMIN pembayaran
 * vendor. Empat berikutnya = mode **produksi sendiri** (mig 4250); semuanya
 * JENIS biaya. `ongkir` dipakai kedua mode dan satu-satunya yang punya
 * perlakuan laba/rugi sendiri (freight-in di `report_profit_loss`).
 */
export type ProductionCostType =
  | "dp1"
  | "dp2"
  | "dp3"
  | "ongkir"
  | "pelunasan"
  | "bahan_baku"
  | "aksesoris"
  | "jasa_produksi"
  | "lain_lain";

/**
 * Mode Biaya Produksi sebuah produk (mig 4250). `supplier` = kulak ke vendor
 * bertermin DP; `produksi` = dijahit sendiri, biayanya per jenis.
 */
export type ProductionCostMode = "supplier" | "produksi";

/** Sumber dana keluar saat membayar biaya produksi (mig 086 + saldo merek mig 154). */
export type ProductionFundSource = "tunai" | "bank" | "saldo_merek";

/** Satu entri ledger biaya produksi internal. */
export type ProductionCostEntry = {
  id: string;
  costType: ProductionCostType;
  amount: number;
  note: string | null;
  /** Sumber dana keluar: tunai / bank (mig 086). NULL untuk entri lama. */
  fundSource: ProductionFundSource | null;
  /** FK rekening bank terpilih (NULL bila tunai / rekening dihapus). */
  paymentAccountId: string | null;
  /** Snapshot label sumber dana (mis. "BCA 12345" / "Tunai"). */
  fundSourceLabel: string | null;
  /**
   * Kategori KUSTOM toko (mig 4251). null = kategori bawaan, dikenali dari
   * `costType`. Kategori yang dihapus meninggalkan null di sini — entri tetap
   * ada dan jatuh ke label embernya, bukan ikut hilang.
   */
  categoryId: string | null;
  /**
   * Rp yang sudah DIKEMBALIKAN vendor ke saldo merek karena gagal produksi
   * (mig 3960). Append-only; barisnya tak pernah dihapus supaya trigger
   * `bank_on_production_cost` tidak ikut membalikkan kas. Sisa utang efektif
   * ke vendor = `amount - refundedAmount`.
   */
  refundedAmount: number;
  /** Tanggal bayar (date ISO "YYYY-MM-DD"). */
  paidAt: string;
  createdAt: string;
  /**
   * Path bukti transfer di bucket privat `payment-proofs` (mig 6480); null =
   * tanpa bukti. OPSIONAL di tipe: hanya pembaca Detail Produksi / Invoice
   * Supplier (`fetchProductionCostsFromSupabase`) yang memintanya — laporan &
   * ekspor tak butuh, jadi absen di sana berarti "tak diambil", bukan "tak ada".
   */
  proofUrl?: string | null;
  /**
   * Porsi satu pembayaran KOMBINASI berbagi uuid ini (mig 6480); null =
   * pembayaran satu sumber dana. Opsional dengan alasan yang sama.
   */
  paymentGroupId?: string | null;
};

export type ProductionArrivalStatus = "pending" | "partial" | "complete";

/** Status kedatangan satu varian di halaman Detail Produksi. */
export type ProductionVariantStatus = "belum" | "partial" | "ready" | "gagal";

export type ProductionDetailVariant = {
  /**
   * id baris product_variants SAAT INI (match by name) — null bila nama varian
   * di order_items tak lagi punya baris varian (varian di-rename/dihapus via
   * ProductForm). Baris ber-id null TIDAK bisa dipilih untuk aksi.
   */
  variantId: string | null;
  variantName: string;
  /** product_variants.sku SAAT INI — null bila varian yatim / SKU belum terisi. */
  sku: string | null;
  /** product_variants.price_normal SAAT INI (null = harga menyusul / belum diisi). */
  priceNormal: number | null;
  /**
   * product_variants.price_extra SAAT INI (mig 7900) — tambahan yang ditempel
   * SETELAH diskon. Absen/0 = tanpa tambahan. Harga Jual Umum varian = dasar +
   * tambahan (`variantOwnTotal`); Atur Harga & Edit Harga Invoice Supplier
   * menyuntingnya terpisah dari harga dasar.
   */
  priceExtra?: number;
  /**
   * product_variants.hpp SAAT INI. **null = tak diminta** (pemirsa tanpa izin
   * `keuangan` — kolomnya memang tak ikut di-select), BUKAN "HPP nol".
   * Dipakai modal Hitung HPP Otomatis untuk pratinjau margin dan untuk menulis
   * ber-DIFF (hanya varian yang nilainya berubah), serta kolom HPP PDF rekap
   * varian (hanya `withFinance`). Tak pernah dirender di layar/berkas yang
   * bebas izin — Excel Rekap Pelanggan & PDF tanpa izin dikunci tes.
   */
  hpp: number | null;
  /** Σ qty item non-cancelled (gagal & cancel lain dikecualikan). */
  qtyActive: number;
  /** Σ qty item cancelled ber-cancel_reason='gagal_produksi'. */
  qtyFailed: number;
  qtyOrdered: number;
  qtyArrived: number;
  arrivalStatus: ProductionArrivalStatus;
  /**
   * Σ qty item ber-`item_status='Produksi'` = pcs yang BENAR-BENAR disubmit ke
   * batch produksi, cermin dasar `qty_ordered` di `tandai_mulai_dikirim` §(b)
   * (mig 2220). NULL = kolom `item_status` tidak diminta pemanggil, jadi tak
   * ada dasar membedakannya dari `qtyActive` (bughunt C3-5).
   */
  qtySubmitted: number | null;
  /**
   * Σ qty item non-cancelled yang BUKAN `item_status='Menunggu'` = peserta batch
   * produksi ini. Cermin filter `mark_variants_production_failed` sejak mig 5110
   * (bughunt C4-2): pcs yang belum disubmit tidak ikut dibatalkan Gagal Produksi.
   * Beda dari `qtySubmitted`, yang hanya menghitung `item_status='Produksi'` dan
   * karenanya jatuh ke 0 begitu batch melewati Tandai Mulai Dikirim.
   * NULL = kolom `item_status` tidak diminta pemanggil (idiom `qtySubmitted`).
   */
  qtyBatch: number | null;
  /** Σ qty item ber-keep aktif (unpaid_keep/paid_keep/hold) = sudah di Keranjang Ready. */
  qtyReadyTagged: number;
  /** Σ qty item requested_shipment/shipped. */
  qtyShipped: number;
  /** Ada pcs sedang/sudah dikirim → blokir Tandai Belum Ready. */
  hasShippedPcs: boolean;
  /**
   * production_status varian (mig 005). Bukan dasar peringatan "belum disubmit"
   * selama `qtySubmitted` tersedia — lihat `variantHasUnsubmittedPcs`.
   */
  productionStatus: "pending" | "partial" | "submitted" | null;
  status: ProductionVariantStatus;
  /** Jumlah pembeli unik (aktif) yang memesan varian ini. Cermin PODetailVariant. */
  resellerCount: number;
  /**
   * Nama + qty per pembeli (aktif) yang memesan varian ini, urut nama. Cermin
   * PODetailVariant, ditambah progres barangnya: `readyQty` = pcs yang sudah
   * ada (Keranjang Ready + diajukan kirim/dikirim), `shippedQty` = bagian yang
   * sudah diajukan kirim/dikirim — bahan badge "Sebagian 2/5" di samping nama
   * pembeli (`productionReadinessBadge`).
   */
  buyers: ProductionVariantBuyer[];
  notes: VariantNoteEntry[];
  /**
   * Item kandidat penerima barang datang varian ini, urut FIFO (waktu order
   * dibuat). Dipakai modal Tandai Ready untuk PRATINJAU pembagian per-pcs
   * (`planReadyAllocation`). Syarat kandidatnya di `isReadyCandidate` —
   * cermin WHERE mesin SQL mig 4750.
   */
  readyCandidates: ReadyCandidate[];
};

/** Satu pembeli (aktif) pada daftar pembeli kartu Rekap Varian. */
export type ProductionVariantBuyer = {
  buyerName: string;
  qty: number;
  /** Pcs yang barangnya sudah ada: ready + dikirim (⊆ qty). */
  readyQty: number;
  /** Pcs yang sudah diajukan kirim/dikirim (⊆ readyQty). */
  shippedQty: number;
};

/** Status satu item pesanan reseller di Detail Produksi (rekap per-reseller). */
export type ProductionItemStatus = "belum" | "ready" | "dikirim" | "gagal";

/** Satu baris pesanan milik seorang reseller pada rekap per-reseller. */
export type ProductionResellerItem = {
  variantName: string;
  qty: number;
  note: string | null;
  status: ProductionItemStatus;
};

/**
 * Rekap satu reseller pada Detail Produksi: daftar varian + qty + catatan +
 * status per baris (tanpa nilai uang). Cermin PODetailReseller (board.ts),
 * ditambah status karena halaman produksi peduli progres tiap item.
 */
export type ProductionDetailReseller = {
  customerId: string | null;
  customerName: string;
  phone: string;
  /** Nama admin handler (customers.assigned_admin_id → profiles). */
  handlerName: string | null;
  /** Σ qty item aktif (non-cancelled). */
  totalQty: number;
  /** Σ qty item gagal produksi (di luar totalQty). */
  failedQty: number;
  /**
   * Stempel "sudah dikabari produk ready via WhatsApp" (mig 4670) — TERBARU di
   * antara order pembeli ini untuk produk tsb. NULL = belum pernah. Penanda
   * saja; ia tidak mengubah pesanan, notifikasi in-app, maupun statusnya.
   */
  readyNotifiedAt: string | null;
  /** Nama admin yang terakhir menandai kabar ready (mig 4670). */
  readyNotifiedByName: string | null;
  items: ProductionResellerItem[];
  /**
   * Level pelanggan (`customers.group_id`) — diskon varian yang berlaku di
   * pesan Konfirmasi & PDF. OPSIONAL di sumbernya: route ekspor Excel tak
   * memintanya (null = Customer biasa / tak diketahui).
   */
  groupId: string | null;
  /**
   * Baris uang item AKTIF (bukan batal/gagal) — bahan pesan Rekap & PDF,
   * aturannya satu dengan Detail PO (`addOrderItemPriceLine`). Kolom uang yang
   * tak diminta pemanggil (ekspor Excel) → harga null = tak dicetak.
   */
  priceLines: PODetailPriceLine[];
};

export type ProductionDetailData = {
  productId: string;
  productName: string;
  brand: string;
  status: string;
  /**
   * Fase Produksi awal: belum ada varian ber-target (qty_ordered>0) & masih
   * ada orders 'Produksi' → Tandai Ready akan otomatis menjalankan
   * tandai_mulai_dikirim dulu (RPC mark_variants_ready, mig 079).
   */
  isPhaseA: boolean;
  /**
   * Ada pesanan berstatus 'Belum Ready' — produk sudah lewat "Tandai Mulai
   * Dikirim" (fase B). Bahan eligibility "Batal Mulai Dikirim" (mig 3390);
   * cermin guard RPC `cancel_tandai_mulai_dikirim`.
   */
  hasBelumReadyOrder: boolean;
  /**
   * Ada varian AKTIF ber-`qty_ordered > 0` — target produksi sudah terkunci
   * "Tandai Mulai Dikirim". Cermin PERSIS gerbang fase A milik RPC
   * `admin_create_production_order` (mig 4110); sengaja bukan turunan
   * `isPhaseA`, yang juga ikut false saat produk tak punya order 'Produksi'
   * sama sekali dan akan memblokir layar di kasus yang RPC-nya izinkan.
   */
  hasProductionTarget: boolean;
  totalActivePcs: number;
  totalOrdered: number;
  totalArrived: number;
  totalFailedPcs: number;
  /** Σ pcs yang sudah masuk Keranjang Ready pembeli (unpaid_keep/paid_keep/hold). */
  totalReadyTagged: number;
  /** Σ pcs yang sudah diminta kirim / dikirim (requested_shipment/shipped). */
  totalShipped: number;
  readyVariantCount: number;
  totalVariantCount: number;
  /** Σ(hpp varian × qty aktif) — estimasi modal produksi (HPP). HPP null → 0. */
  totalHpp: number;
  /**
   * Override TOTAL PRODUKSI (HPP) yang diisi admin (products.production_target_cost,
   * mig 086). NULL → pakai estimasi `totalHpp`.
   */
  targetCost: number | null;
  /** products.price_normal (harga produk, fallback storefront). null = belum diisi. */
  productPriceNormal: number | null;
  /**
   * Rentang harga UTUH (dasar + tambahan, mig 7900 — `variantOwnTotal`)
   * SELURUH baris varian produk (bukan hanya yang dipesan) — cermin agregat
   * toko `product_variant_price_range` (mig 5450; `min_total`/`max_total`).
   * null = belum ada varian berharga. Bahan `variantSellingPrice`: penentu
   * apakah varian tanpa harga sendiri memakai harga saudaranya (seragam) atau
   * tetap harga menyusul (campuran).
   */
  variantPriceRange: VariantPriceSummary | null;
  /**
   * Override mode Biaya Produksi produk ini (mig 4250); null = ikut default
   * toko. Mode EFEKTIF sengaja TIDAK dihitung di sini — defaultnya datang dari
   * site settings, bukan dari baris produk, dan modul ini murni (tanpa I/O).
   * Pemakainya menggabungkan lewat `resolveProductionCostMode`.
   */
  costModeOverride: ProductionCostMode | null;
  /**
   * products.hpp_mode. null = tak diminta (tanpa izin `keuangan`). Non-`direct`
   * berarti `product_variants.hpp` DITURUNKAN dari harga tiap kali produk
   * disimpan — menulisnya langsung dari Hitung HPP akan tertimpa senyap.
   */
  hppMode: string | null;
  /**
   * Produk ber-SUMBU harga (`price_affecting_groups`, mig 7680)? null = tak
   * diketahui (kolom tak dimuat). Pada produk ber-sumbu, varian tanpa harga
   * sendiri tak ikut harga seragam / harga produk — bahan `variantSellingPrice`
   * untuk badge, PDF rekap, margin HPP, Invoice Supplier, dan teks Atur Harga.
   */
  priceAxis: boolean | null;
  /** Ada varian dipesan (qtyActive>0) tanpa harga efektif → perlu "Atur Harga". */
  needsPrice: boolean;
  /** Teks estimasi ready produk (pesan Konfirmasi); null = tak diisi/diminta. */
  estimatedReady: string | null;
  /**
   * Harga umum + setelan diskon SELURUH varian (termasuk nonaktif) — bahan
   * pesan Konfirmasi & PDF, cermin `PODetailData.variantPricing`.
   */
  variantPricing: VariantPricing[];
  variants: ProductionDetailVariant[];
  resellers: ProductionDetailReseller[];
};

// Bentuk baris mentah dari Supabase (sebelum diagregasi). DIEKSPOR karena
// route server (/api/admin/production-reseller-export) merakit sendiri bentuk
// ini dari query-nya sebelum memanggil `aggregateProductionDetail`.
export type ProductionHeaderRow = {
  id: string;
  name: string;
  brand: string | null;
  status: string;
  price_normal: number | null;
  production_target_cost: number | null;
  /**
   * Override mode Biaya Produksi produk ini (mig 4250). NULL = ikut default
   * toko. OPSIONAL supaya route server yang merakit bentuk ini sendiri
   * (/api/admin/production-reseller-export) tak perlu ikut memintanya.
   */
  production_cost_mode?: string | null;
  /**
   * products.hpp_mode — penjaga tombol Hitung HPP Otomatis. Hanya diminta saat
   * pemirsanya pegang izin `keuangan`, jadi OPSIONAL di sini.
   */
  hpp_mode?: string | null;
  /**
   * Bahan pesan Rekap & PDF per pelanggan. OPSIONAL (juga kolom diskon
   * varian di bawah): route ekspor Excel merakit bentuk ini sendiri tanpa
   * memintanya — absennya cuma berarti pesannya tanpa estimasi/diskon.
   */
  estimated_ready?: string | null;
  /**
   * products.price_affecting_groups (mig 7680) — bahan `priceAxis`. OPSIONAL:
   * route ekspor tak memintanya; absen = sumbu tak diketahui.
   */
  price_affecting_groups?: string[] | null;
  product_variants:
    | {
        id: string;
        name: string;
        sku: string | null;
        sort_order: number | null;
        is_active: boolean | null;
        qty_ordered: number | null;
        qty_arrived: number | null;
        arrival_status: string | null;
        production_status: string | null;
        hpp: number | null;
        price_normal: number | null;
        /** mig 7900: tambahan per varian — absen bila pemanggil tak memintanya. */
        price_extra?: number | string | null;
        diskon_mode?: string | null;
        diskon_reseller?: number | string | null;
        diskon_agen?: number | string | null;
        diskon_slot3?: number | string | null;
        diskon_slot4?: number | string | null;
        diskon_slot5?: number | string | null;
        diskon_slot6?: number | string | null;
        diskon_slot7?: number | string | null;
        diskon_slot8?: number | string | null;
        diskon_slot9?: number | string | null;
        diskon_slot10?: number | string | null;
        diskon_slot11?: number | string | null;
        diskon_slot12?: number | string | null;
        diskon_slot13?: number | string | null;
        diskon_slot14?: number | string | null;
        diskon_slot15?: number | string | null;
      }[]
    | null;
};

export type ProductionOrderRow = {
  id: string;
  status: string;
  customer_id: string | null;
  customer_name: string | null;
  phone: string | null;
  created_at: string | null;
  /**
   * Embed customers → profiles (nama admin handler) + `group_id` (level,
   * OPSIONAL — ekspor Excel tak memintanya). Bisa objek atau array.
   */
  customers?:
    | { group_id?: string | null; profiles?: { full_name: string | null } | null }
    | { group_id?: string | null; profiles?: { full_name: string | null } | null }[]
    | null;
  /**
   * Penanda kabar ready via WhatsApp (mig 4670). OPSIONAL: route ekspor Excel
   * merakit bentuk baris ini sendiri dan tak meminta kolomnya — absennya cuma
   * berarti kartunya tak pernah bertanda, bukan bentuk yang salah.
   */
  ready_notified_at?: string | null;
  /** Embed profiles lewat FK `orders_ready_notified_by_fkey` (mig 4670). */
  ready_notified_profile?:
    | { full_name: string | null }
    | { full_name: string | null }[]
    | null;
  /** Kolom uang (`PriceLineSourceItem`) OPSIONAL — ekspor Excel tak memintanya. */
  order_items:
    | ({
        id: string;
        variant_name: string;
        qty: number | null;
        item_note: string | null;
        keep_status: string | null;
        cancel_reason: string | null;
        created_at: string | null;
        /**
         * OPSIONAL: route ekspor Excel merakit bentuk baris ini sendiri dan tak
         * memintanya. Absennya berarti "tidak diketahui" — `isReadyCandidate`
         * memperlakukannya sebagai peserta batch (perilaku sebelum mig 4750),
         * bukan menebaknya 'Menunggu' lalu membuang barisnya diam-diam.
         */
        item_status?: string | null;
        /** OPSIONAL, alasan yang sama. */
        shipment_request_id?: string | null;
        /**
         * OPSIONAL (mig 7760): baris PAKET yang masih menunggu barang
         * (`requested_shipment` ber-`ready_at` NULL) ikut antre kedatangan.
         * Absen = tak diketahui → baris paket dibaca seperti dulu (sudah
         * menyerap kedatangan, bukan kandidat).
         */
        ready_at?: string | null;
      } & Partial<Omit<PriceLineSourceItem, "variant_name">>)[]
    | null;
};

// ─── Fungsi murni ─────────────────────────────────────────────────────────────

const TAGGED_KEEPS = new Set(["unpaid_keep", "paid_keep", "hold"]);
const SHIPPED_KEEPS = new Set(["requested_shipment", "shipped"]);

/**
 * Satu item pesanan berhak ikut antre pembagian barang datang — cermin PERSIS
 * klausa WHERE kandidat FIFO di `tag_ready_items_and_check_completion`
 * (mig 4750, diperluas mig 7760):
 *
 * ```sql
 *   and o.status                <> 'Batal'
 *   and (
 *     (oi.keep_status is null and oi.shipment_request_id is null)
 *     or (oi.keep_status = 'requested_shipment' and oi.ready_at is null)
 *   )
 *   and oi.item_status          <> 'Menunggu'
 * ```
 *
 * Cabang kedua (mig 7760) = baris PAKET yang menunggu barang: Request Kirim /
 * Nota + Kirim sebelum ready. `ready_at` yang ABSEN dibaca fail-closed untuk
 * cabang itu (bukan kandidat) — pratinjau yang menjanjikan jatah lebih buruk
 * daripada yang menahannya.
 *
 * Dikumpulkan jadi satu predikat bernama supaya cermin TS dan mesin SQL tidak
 * bisa lagi menyimpang diam-diam: sebelum ini keempat syarat itu tersebar,
 * dua di antaranya bahkan tidak ditiru sama sekali dan kesepadanannya cuma
 * bersandar pada konvensi "pembatalan selalu menulis keep_status='cancelled'"
 * (bughunt C3-7).
 *
 * `item_status`/`shipment_request_id` yang ABSEN (bukan null) = kolomnya tak
 * diminta pemanggil; itu diperlakukan sebagai lolos, bukan gugur.
 *
 * Syarat kelima SQL — `p.product_type = 'preorder'` — sengaja di luar predikat:
 * agregat ini selalu dipanggil untuk SATU produk yang sudah pasti preorder
 * (halaman Detail Produksi), jadi menyalinnya cuma menambah argumen tanpa
 * menambah jaminan.
 */
export function isReadyCandidate(
  item: {
    qty: number | null;
    keep_status: string | null;
    item_status?: string | null;
    shipment_request_id?: string | null;
    ready_at?: string | null;
  },
  order: { status: string }
): boolean {
  if (order.status === "Batal") return false;
  if (item.keep_status == null) {
    if (item.shipment_request_id != null) return false;
  } else if (!isWaitingPackageRow(item)) {
    return false;
  }
  if (item.item_status === "Menunggu") return false;
  return (item.qty ?? 0) > 0;
}

/**
 * Baris PAKET yang masih menunggu barang (mig 7760): `requested_shipment`
 * ber-`ready_at` NULL (bukan absen). Ia belum menyerap kedatangan dan masih
 * ikut antre — cermin `v_consumed` & kandidat di `tag_ready_items_and_check_
 * completion`, dan `shipment_goods_readiness`.
 */
export function isWaitingPackageRow(item: {
  keep_status: string | null;
  ready_at?: string | null;
}): boolean {
  return item.keep_status === "requested_shipment" && item.ready_at === null;
}

/**
 * Nilai `order_items.item_status` yang menandai pcs SUDAH disubmit ke batch
 * produksi. `tandai_mulai_dikirim` §(b) (mig 2220) mengisi
 * `product_variants.qty_ordered` dari persis baris-baris ini, jadi Σ qty-nya
 * (`ProductionDetailVariant.qtySubmitted`) adalah target fase A yang benar —
 * `qtyActive` ikut menghitung pcs belum-disubmit dan karenanya membuat batas
 * input & pratinjau lebih longgar dari yang akan diterima RPC (bughunt C3-5).
 */
const SUBMITTED_ITEM_STATUS = "Produksi";
/** Belum disubmit ke batch produksi — bukan peserta (mig 5110, bughunt C4-2). */
const WAITING_ITEM_STATUS = "Menunggu";

/**
 * Status tampilan satu varian:
 * - `gagal`  — seluruh pcs-nya gagal produksi (tak ada item aktif tersisa).
 * - `ready`  — kedatangan complete, ATAU semua pcs aktif sudah ter-tag/terkirim
 *              (meng-cover varian yang sudah terkirim penuh, dan data lama yang
 *              ter-tag keep tanpa mengisi tracking qty — mis. jalur bulk lama
 *              mark_product_received sebelum dihapus mig 093).
 * - `partial`— sebagian sudah tiba / ter-tag.
 * - `belum`  — belum ada yang tiba.
 */
export function deriveVariantStatus(
  v: Pick<
    ProductionDetailVariant,
    "qtyActive" | "qtyFailed" | "arrivalStatus" | "qtyArrived" | "qtyReadyTagged" | "qtyShipped"
  >
): ProductionVariantStatus {
  if (v.qtyActive === 0 && v.qtyFailed > 0) return "gagal";
  if (v.arrivalStatus === "complete") return "ready";
  if (v.qtyActive > 0 && v.qtyReadyTagged + v.qtyShipped >= v.qtyActive) return "ready";
  if (v.qtyArrived > 0 || v.qtyReadyTagged > 0) return "partial";
  return "belum";
}

/**
 * Harga Jual Umum satu varian — aturan yang sama dengan toko
 * (`resolveSelectionPrice` + `sellableSelectionPrice`, pricing.ts). Dipakai
 * dokumen (PDF rekap varian), badge "Harga Belum Ditentukan", dan margin HPP:
 *
 *   1. harga varian sendiri;
 *   2. produk BELUM punya varian berharga → `products.price_normal` berlaku
 *      untuk semua varian (alur harga menyusul biasa);
 *   3. varian berharga SERAGAM → harga itu (perwakilan pasti benar);
 *   4. varian berharga BERBEDA-BEDA → null = harga menyusul. Toko TIDAK
 *      menagihkan harga varian lain untuk varian ini (#2095), jadi dokumen
 *      pun tak boleh memajangnya.
 *
 * Kenapa bukan "harga varian ?? harga produk": form menyimpan
 * `products.price_normal` sebagai MIN harga seluruh varian, jadi pada produk
 * berharga campuran fallback itu memajang harga TERMURAH untuk varian yang
 * bisa jauh lebih mahal. PROD 2026-09-25: 20 varian dipesan di 10 produk
 * campuran tanpa harga sendiri — semuanya ber-`unit_price` NULL (ditagih
 * sebagai harga menyusul), dan warisan slotnya (`preorder_inherited_pricing`)
 * pun kosong.
 *
 * SUMBU HARGA (mig 7680, #1429): pada produk ber-`price_affecting_groups`
 * keseragaman varian berharga BUKAN bukti — baru satu nilai sumbu yang
 * berharga pun sudah "seragam" (Mukena 17 Sep: NAJWA dikunci harga RAJWA).
 * Checkout memberi varian tanpa harga sendiri HANYA warisan slot, selain itu
 * harga menyusul. `priceAxis` wajib diisi pemanggil: `true` atau `null` (tak
 * diketahui, fail-closed) → varian tanpa harga sendiri = null; `false` →
 * aturan 2–4 di atas.
 *
 * Warisan slot (Fase 4, mig 5510) sengaja tak ditiru: angkanya hanya boleh
 * datang dari RPC itu ("jangan menghitung harga slot di TypeScript",
 * pricing.ts). Tanpanya varian seperti ini tercetak "—" — kosong, bukan angka
 * yang salah.
 */
export function variantSellingPrice(
  variantPrice: number | null,
  productPrice: number | null,
  variantPriceRange: VariantPriceSummary | null,
  priceAxis: boolean | null,
  /** Tambahan varian ini SENDIRI (mig 7900) — penentu pinjaman, bughunt D14-2. */
  ownExtra = 0
): number | null {
  if (variantPrice != null) return variantPrice;
  if (priceAxis !== false) return null;
  // Pinjaman membawa tambahan pemberinya: sah hanya bila sepakat dengan tambahan
  // varian ini — aturan yang SAMA dengan toko (`resolveSelectionPrice`).
  if (!borrowedExtraAgrees(Math.max(0, ownExtra || 0), variantPriceRange)) return null;
  if (variantPriceRange == null) return productPrice;
  return variantPriceRange.min === variantPriceRange.max
    ? variantPriceRange.min
    : null;
}

/**
 * Harga jual UTUH milik varian sendiri = dasar + tambahan (mig 7900); null =
 * tanpa harga dasar (harga menyusul — tambahannya belum berarti apa-apa).
 *
 * Bahan WAJIB `variantSellingPrice` (argumen pertama) DAN `variantPriceRangeOf`:
 * keduanya harus memegang angka utuh supaya "seragam" dinilai sama dengan toko
 * (`variantPriceSpread`, pricing.ts) — dasar seragam dengan tambahan berbeda
 * tetap CAMPURAN, dan varian tanpa harga sendiri tak boleh meminjam tambahan
 * ukuran lain (kelas #2095). Lapis harga produk (`products.price_normal`) tak
 * pernah membawa tambahan — sama dengan toko.
 */
export function variantOwnTotal(
  base: number | string | null | undefined,
  extra: number | string | null | undefined
): number | null {
  if (base === null || base === undefined || base === "") return null;
  const b = Number(base);
  if (!Number.isFinite(b)) return null;
  const e = extra === null || extra === undefined || extra === "" ? 0 : Number(extra);
  return b + (Number.isFinite(e) && e > 0 ? e : 0);
}

/**
 * Ringkasan harga baris varian = rentang harga UTUH (`variantOwnTotal`) +
 * rentang TAMBAHAN baris berharga — bahan `variantSellingPrice`, yang menguji
 * apakah varian tanpa harga sendiri boleh meminjam total seragam (tambahannya
 * wajib sepakat, bughunt D14-2). Sama dengan `variantPriceRangeOf`: WAJIB
 * SELURUH baris varian produk. null = belum ada varian berharga.
 */
export function variantPriceSummaryOf(
  rows: Iterable<{
    price_normal: number | string | null | undefined;
    price_extra?: number | string | null | undefined;
  }>
): VariantPriceSummary | null {
  let out: VariantPriceSummary | null = null;
  for (const r of rows) {
    const total = variantOwnTotal(r.price_normal, r.price_extra);
    if (total === null) continue;
    const extra = total - Number(r.price_normal);
    out = out
      ? {
          min: Math.min(out.min, total),
          max: Math.max(out.max, total),
          extraMin: Math.min(out.extraMin ?? extra, extra),
          extraMax: Math.max(out.extraMax ?? extra, extra)
        }
      : { min: total, max: total, extraMin: extra, extraMax: extra };
  }
  return out;
}

/** Tambahan baris varian SENDIRI (≥ 0) — argumen ke-5 `variantSellingPrice`. */
export function variantOwnExtra(extra: number | string | null | undefined): number {
  if (extra === null || extra === undefined || extra === "") return 0;
  const e = Number(extra);
  return Number.isFinite(e) && e > 0 ? e : 0;
}

/**
 * Rentang `price_normal` dari baris varian — bahan `variantSellingPrice`.
 * WAJIB diberi SELURUH baris varian produk (termasuk nonaktif, cermin agregat
 * `product_variant_price_range` mig 5450); subset membuat harga campuran
 * terbaca "seragam". null = belum ada varian berharga.
 */
export function variantPriceRangeOf(
  prices: Iterable<number | string | null | undefined>
): { min: number; max: number } | null {
  let min: number | null = null;
  let max: number | null = null;
  for (const raw of prices) {
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    if (min === null || n < min) min = n;
    if (max === null || n > max) max = n;
  }
  return min !== null && max !== null ? { min, max } : null;
}

/**
 * Produk "perlu harga" bila ADA varian yang dipesan (qtyActive>0) tanpa Harga
 * Jual Umum menurut aturan toko (`variantSellingPrice`). Varian tanpa pesanan /
 * gagal-total (qtyActive=0) diabaikan agar badge "Harga Belum Ditentukan" tak
 * menyala selamanya.
 *
 * `variantPriceRange` WAJIB rentang SELURUH baris varian. Dulu fallback-nya
 * "harga varian ?? harga produk", sehingga produk berharga campuran tak pernah
 * menyalakan badge: `products.price_normal` = harga termurah selalu terisi.
 * PROD 2026-10-20 (KUSUMA AYU FAMILY SERIES BATCH 2, Rp 95.000–374.000): 7
 * varian dipesan tanpa harga sendiri, `unit_price` pesanannya NULL (ditagih
 * harga menyusul), tapi tombol Atur Harga & kartu Tab Produksi diam.
 */
export function productNeedsPrice(
  variants: { qtyActive: number; priceNormal: number | null; priceExtra?: number }[],
  productPrice: number | null,
  variantPriceRange: VariantPriceSummary | null,
  priceAxis: boolean | null
): boolean {
  return variants.some(
    (v) =>
      v.qtyActive > 0 &&
      variantSellingPrice(
        variantOwnTotal(v.priceNormal, v.priceExtra),
        productPrice,
        variantPriceRange,
        priceAxis,
        v.priceExtra
      ) == null
  );
}

/** Boleh masuk daftar "Tandai Ready"? (fase A: ada pcs aktif; fase B: target belum penuh) */
export function canMarkReady(
  v: Pick<ProductionDetailVariant, "variantId" | "status" | "qtyActive" | "qtyOrdered" | "qtyArrived">,
  isPhaseA: boolean
): boolean {
  if (!v.variantId) return false;
  if (v.status === "ready" || v.status === "gagal") return false;
  return isPhaseA ? v.qtyActive > 0 : v.qtyOrdered - v.qtyArrived > 0;
}

/**
 * Peringatan di kartu varian Detail Produksi. Dua keadaan yang dulu tampil
 * sebagai SATU label "Belum disubmit penuh ke produksi":
 *
 * - `yatim` — ada pcs aktif, tapi nama varian di pesanannya tak cocok dengan
 *   baris varian mana pun (`variantId` null). Jalur produksi mencocokkan lewat
 *   NAMA, jadi pcs ini tak bisa dicentang dan tak ikut target produksi. Kasus
 *   nyata 2026-09-24 (galleryhasanah, KP2609230007): form admin memegang nama
 *   basi sesudah grup variasi ditata ulang, lalu disangka "belum disubmit" dan
 *   di-Batal Submit/Submit ulang berkali-kali. Penulisnya ditutup mig 6240;
 *   label ini memastikan sisa kasus serupa terbaca apa adanya.
 * - `belum-submit` — fase A, varian dikenali, masih ada pcs aktif di luar batch
 *   produksi (`variantHasUnsubmittedPcs`).
 */
export type ProductionVariantWarning = "yatim" | "belum-submit" | null;

export function productionVariantWarning(
  v: Pick<ProductionDetailVariant, "variantId" | "productionStatus" | "qtyActive" | "qtySubmitted">,
  isPhaseA: boolean
): ProductionVariantWarning {
  if (v.qtyActive <= 0) return null;
  if (!v.variantId) return "yatim";
  if (isPhaseA && variantHasUnsubmittedPcs(v)) return "belum-submit";
  return null;
}

/**
 * Varian masih punya pcs aktif yang BELUM masuk batch produksi? Diukur dari
 * pcs-nya sendiri: pcs aktif lebih banyak dari pcs ber-`item_status='Produksi'`
 * (`qtySubmitted`) — dasar yang sama dengan target fase A Tandai Ready
 * (`defaultReadyQty`, tandai_mulai_dikirim §(b)). Dipakai label kartu varian
 * DAN daftar peringatan modal Tandai Ready, supaya keduanya selalu sepakat.
 *
 * `production_status` varian sengaja bukan dasarnya lagi: pesanan manual saat
 * produksi (admin_create_production_order, mig 4110) lahir langsung 'Produksi'
 * pada varian yang tetap 'pending'. PROD 2026-10-07: 75 varian di 63 produk
 * berlabel "belum disubmit penuh" padahal semua pcs-nya sudah di batch — nol
 * peringatan yang benar. `qtySubmitted` null (kolom `item_status` tak diminta
 * pemanggil) → jatuh balik ke `production_status`, perilaku lama.
 */
export function variantHasUnsubmittedPcs(
  v: Pick<ProductionDetailVariant, "productionStatus" | "qtyActive" | "qtySubmitted">
): boolean {
  if (v.qtySubmitted == null) return v.productionStatus !== "submitted";
  return v.qtySubmitted < v.qtyActive;
}

/** Boleh di-undo ("Tandai Belum Ready")? Diblokir bila ada pcs dikirim. */
export function canMarkNotReady(
  v: Pick<ProductionDetailVariant, "variantId" | "hasShippedPcs" | "qtyArrived" | "qtyReadyTagged">
): boolean {
  if (!v.variantId) return false;
  if (v.hasShippedPcs) return false;
  return v.qtyArrived > 0 || v.qtyReadyTagged > 0;
}

/**
 * Boleh ikut aksi massal **Cetak Barcode** (label per-pcs varian yang ditandai)?
 *
 * Syaratnya persis apa yang membuat sebuah label benar-benar tercetak:
 * - punya baris varian (`variantId`) — varian yatim tak bisa dicentang di UI,
 *   dan meta SKU-nya memang tak ada;
 * - punya **SKU** — QR label di-generate DARI SKU, jadi varian tanpa SKU selalu
 *   di-skip `expandBarcodeLabels` (`skippedNoSku`);
 * - ada **pcs aktif** — label = satu per pcs pesanan non-cancelled; varian yang
 *   seluruh pcs-nya gagal produksi menghasilkan nol label.
 *
 * Sengaja TIDAK melihat status kedatangan: barcode berguna di semua fase
 * (dicetak sebelum barang datang untuk ditempel saat sortir, atau dicetak ulang
 * setelah Ready). Cermin dari yang dihitung `filterBarcodeDataByVariants` +
 * `expandBarcodeLabels`, supaya angka di tombol = jumlah label yang nyata.
 */
export function canPrintBarcode(
  v: Pick<ProductionDetailVariant, "variantId" | "sku" | "qtyActive">
): boolean {
  if (!v.variantId) return false;
  if (!v.sku) return false;
  return v.qtyActive > 0;
}

/**
 * Pcs yang BENAR-BENAR akan dibatalkan "Gagal Produksi" — cermin himpunan
 * `v_item_ids` di `mark_variants_production_failed` sejak mig 5110 (bughunt C4-2):
 * peserta batch (`item_status <> 'Menunggu'`) dikurangi pcs yang sudah jalan kirim
 * (`requested_shipment`/`shipped`, memang dikecualikan RPC) dan — sejak mig 7990 —
 * pcs yang SUDAH DATANG di Keranjang Ready (`qtyReadyTagged`, `ready_at` terisi):
 * barangnya nyata, tetap milik pembeli, tidak ikut dibatalkan.
 *
 * Memakai `qtyActive` di sini membuat layar menjanjikan pembatalan pcs yang
 * belum pernah disubmit ke vendor — angka yang tak akan pernah terjadi sejak
 * mig 5110. `qtyBatch` null (kolom `item_status` tak diminta) → jatuh balik ke
 * `qtyActive`, idiom `defaultReadyQty`.
 */
export function variantFailablePcs(
  v: Pick<ProductionDetailVariant, "qtyActive" | "qtyBatch" | "qtyShipped" | "qtyReadyTagged">
): number {
  return Math.max(0, (v.qtyBatch ?? v.qtyActive) - v.qtyShipped - v.qtyReadyTagged);
}

/** Boleh ditandai Gagal Produksi? (masih ada pcs peserta batch yang belum datang/jalan kirim) */
export function canMarkFailed(
  v: Pick<ProductionDetailVariant, "variantId" | "qtyActive" | "qtyBatch" | "qtyShipped" | "qtyReadyTagged">
): boolean {
  if (!v.variantId) return false;
  return variantFailablePcs(v) > 0;
}

/**
 * Eligibility tombol "Batal Submit ke Produksi" (mig 118): produk masih di FASE
 * AWAL produksi — `PROSES PRODUKSI` + `isPhaseA` (belum mulai dikirim / belum ada
 * kedatangan). Cermin guard RPC cancel_submit_to_production.
 */
export function canCancelSubmitProduction(
  data: Pick<ProductionDetailData, "status" | "isPhaseA">
): boolean {
  return data.status === "PROSES PRODUKSI" && data.isPhaseA;
}

/** Data minimal untuk menilai fase & kelayakan "Batal Mulai Dikirim". */
export type CancelMulaiDikirimInput = Pick<
  ProductionDetailData,
  | "status"
  | "isPhaseA"
  | "hasBelumReadyOrder"
  | "totalArrived"
  | "totalReadyTagged"
  | "totalShipped"
>;

/**
 * Produk sudah lewat "Tandai Mulai Dikirim" (FASE B): masih 'PROSES PRODUKSI',
 * target qty per varian sudah terisi (bukan fase A), dan pesanannya berstatus
 * 'Belum Ready'. Dipakai UI untuk memutuskan apakah kotak "Batal Mulai Dikirim"
 * perlu dirender sama sekali.
 */
export function isProductionPhaseB(
  data: Pick<ProductionDetailData, "status" | "isPhaseA" | "hasBelumReadyOrder">
): boolean {
  return (
    data.status === "PROSES PRODUKSI" && !data.isPhaseA && data.hasBelumReadyOrder
  );
}

/**
 * Alasan "Batal Mulai Dikirim" (mig 3390) BELUM bisa dijalankan — null = boleh.
 * Cermin guard RPC `cancel_tandai_mulai_dikirim`, ditulis sebagai kalimat siap
 * tampil supaya tombol yang mati selalu menjelaskan dirinya sendiri (idiom
 * `dpIssueBlockReason` di `dp.ts`) alih-alih gagal senyap saat ditekan.
 *
 * Urutan cek = dari yang paling "keras" (pcs sudah jalan kirim, tak bisa
 * diundo dari sini) ke yang tinggal satu langkah lagi (tinggal Tandai Belum
 * Ready), supaya pesan yang muncul selalu hambatan terdekat yang harus diurus.
 */
export function cancelMulaiDikirimBlockReason(
  data: CancelMulaiDikirimInput
): string | null {
  if (data.status !== "PROSES PRODUKSI") {
    return `Produk berstatus ${data.status}. Jalankan "Tandai Belum Ready" dulu supaya produk kembali ke Proses Produksi.`;
  }
  if (data.isPhaseA) {
    return "Produk masih di fase Dalam Produksi — belum pernah ditandai Mulai Dikirim.";
  }
  if (!data.hasBelumReadyOrder) {
    return "Tidak ada pesanan di fase Belum Ready yang bisa dikembalikan.";
  }
  if (data.totalShipped > 0) {
    return `Ada ${data.totalShipped} pcs yang sudah diminta kirim / dikirim — batalkan pengirimannya dulu.`;
  }
  if (data.totalReadyTagged > 0) {
    return `Ada ${data.totalReadyTagged} pcs yang sudah masuk Keranjang Ready pembeli — jalankan "Tandai Belum Ready" dulu untuk varian tersebut.`;
  }
  if (data.totalArrived > 0) {
    return `Masih ada ${data.totalArrived} pcs barang datang yang tercatat — jalankan "Tandai Belum Ready" dulu untuk varian tersebut.`;
  }
  return null;
}

/**
 * Eligibility tombol "Batal Mulai Dikirim" (mig 3390): produk fase B, belum ada
 * kedatangan / barang ter-tag Ready / pcs dikirim. Setelah aksi ini berhasil
 * produk balik ke fase A sehingga "Batal Submit ke Produksi" tersedia lagi.
 */
export function canCancelMulaiDikirim(data: CancelMulaiDikirimInput): boolean {
  return cancelMulaiDikirimBlockReason(data) === null;
}

/** Data minimal untuk menilai kelayakan "Tambah Pesanan Manual" di Produksi. */
export type ProductionManualOrderInput = Pick<
  ProductionDetailData,
  "status" | "hasProductionTarget"
>;

/**
 * Kunci izin tombol "Tambah Pesanan Manual" Detail Produksi (mig 6410) —
 * dipisah dari `pesanan.kelola` yang membuka halamannya, supaya yang bisa
 * menginput pesanan susulan saat produksi hanya admin yang ditunjuk pemilik.
 */
export const PRODUCTION_MANUAL_ORDER_PERMISSION: AdminGrantKey =
  "pesanan.manual-produksi";

/**
 * Status izin di atas persis seperti dipulangkan `useAdminPermission`. Ditulis
 * ulang di sini, BUKAN diimpor dari adminAccess.ts: modul itu ber-"use client",
 * sedangkan berkas ini wajib aman dipakai route server.
 */
export type ProductionManualOrderAccess = "loading" | "allowed" | "denied";

/**
 * Alasan "Tambah Pesanan Manual" BELUM bisa dipakai di Detail Produksi —
 * null = boleh. Cermin gerbang RPC `admin_create_production_order` (mig 4110,
 * + guard izin mig 6410), ditulis sebagai kalimat siap tampil supaya tombol
 * yang mati menjelaskan dirinya sendiri (idiom `cancelMulaiDikirimBlockReason`).
 *
 * IZIN diperiksa LEBIH DULU — urutan yang sama dengan RPC-nya (guard duduk
 * tepat setelah BEGIN, sebelum gerbang status & fase). "loading" = belum
 * boleh: arah fail-closed `adminAccess.ts`.
 *
 * Fase B ditolak, BUKAN ditambal: di sana `product_variants.qty_ordered` sudah
 * jadi target yang dipakai "Tandai Ready", dan menaikkannya dari alur pesanan
 * berarti penulis kedua untuk kolom itu. Jalan keluarnya eksplisit —
 * "Batal Mulai Dikirim" (mig 3390) memundurkan produk ke fase A.
 */
export function productionManualOrderBlockReason(
  data: ProductionManualOrderInput,
  access: ProductionManualOrderAccess
): string | null {
  if (access === "loading") {
    return "Memeriksa izin…";
  }
  if (access !== "allowed") {
    return `Butuh izin “${permissionLabel(PRODUCTION_MANUAL_ORDER_PERMISSION)}” — minta Super Admin mencentangnya di Manajemen Admin › Perizinan Admin.`;
  }
  if (data.status !== "PROSES PRODUKSI") {
    return `Pesanan manual dari layar ini hanya untuk produk Proses Produksi (status saat ini: ${data.status}).`;
  }
  if (data.hasProductionTarget) {
    return 'Target qty per varian sudah terkunci "Mulai Dikirim" — jalankan "Batal Mulai Dikirim" dulu supaya pesanan susulan ikut terhitung.';
  }
  return null;
}

/**
 * Eligibility tombol "Tambah Pesanan Manual" (mig 4110 + 6410): admin memegang
 * izin `pesanan.manual-produksi`, produk PROSES PRODUKSI yang masih fase A.
 * Gerbang otoritatifnya tetap di RPC — ini yang memutuskan tombolnya hidup
 * atau mati beserta alasannya.
 */
export function canAddProductionManualOrder(
  data: ProductionManualOrderInput,
  access: ProductionManualOrderAccess
): boolean {
  return productionManualOrderBlockReason(data, access) === null;
}

/**
 * Kunci izin tombol "Edit" kartu Rekap Per Pelanggan Detail Produksi (mig 7320)
 * — terpisah dari izin Tambah (`pesanan.manual-produksi`) dan TANPA backfill:
 * bawaannya hanya Super Admin (putusan pemilik 2026-10-15).
 */
export const PRODUCTION_EDIT_ORDER_PERMISSION: AdminGrantKey =
  "pesanan.edit-produksi";

/**
 * Alasan tombol "Edit" pesanan di Detail Produksi BELUM bisa dipakai — null =
 * boleh. Cermin gerbang tingkat PRODUK RPC `admin_edit_production_order`
 * (mig 7320), dengan urutan yang sama: izin dulu, lalu status, lalu fase.
 * Gembok tingkat PESANAN (nota, DP, voucher, harga manual) dinilai terpisah
 * oleh `productionOrderEditLockedReason`, setelah pesanannya dimuat.
 */
export function productionEditOrderBlockReason(
  data: ProductionManualOrderInput,
  access: ProductionManualOrderAccess
): string | null {
  if (access === "loading") {
    return "Memeriksa izin…";
  }
  if (access !== "allowed") {
    return `Butuh izin “${permissionLabel(PRODUCTION_EDIT_ORDER_PERMISSION)}” — minta Super Admin mencentangnya di Manajemen Admin › Perizinan Admin.`;
  }
  if (data.status !== "PROSES PRODUKSI") {
    return `Edit pesanan dari layar ini hanya untuk produk Proses Produksi (status saat ini: ${data.status}).`;
  }
  if (data.hasProductionTarget) {
    return 'Target qty per varian sudah terkunci "Mulai Dikirim" — jalankan "Batal Mulai Dikirim" dulu supaya pesanan bisa diedit.';
  }
  return null;
}

/** Eligibility tombol "Edit" kartu Rekap Per Pelanggan (mig 7320). */
export function canEditProductionOrder(
  data: ProductionManualOrderInput,
  access: ProductionManualOrderAccess
): boolean {
  return productionEditOrderBlockReason(data, access) === null;
}

/** Satu baris `order_items` mentah untuk menilai gembok Edit pesanan produksi. */
export type ProductionEditItemRow = {
  itemStatus: string | null;
  keepStatus: string | null;
  paidQty: number;
  /** Baris tercantum di nota aktif (`CART_INVOICE_ACTIVE_STATUSES`). */
  inActiveInvoice: boolean;
  voucherUsageId: string | null;
  manualPriceNormal: number | null;
  manualDiscountMode: string | null;
};

/**
 * Baris yang DITULIS ULANG Edit pesanan produksi — persis WHERE RPC-nya:
 * `item_status='Produksi' AND keep_status IS NULL`. Baris lain (batal, dsb.)
 * tak disentuh dan tak ikut editor.
 */
export function isProductionEditableRow(
  row: Pick<ProductionEditItemRow, "itemStatus" | "keepStatus">
): boolean {
  return row.itemStatus === "Produksi" && row.keepStatus == null;
}

/** Satu nota DP (`invoices.kind='dp'`) milik pesanan — bahan gembok DP. */
export type ProductionDpInvoiceRow = {
  status: string;
  amountPaid: number;
  balanceApplied: number;
  refundedAmount: number;
  /** Ada `invoice_payments` berstatus `pending` (pembeli sudah lapor transfer). */
  hasPendingPayment: boolean;
};

/**
 * Cermin PERSIS `order_dp_invoice_locked(order_id)` (mig 5340/6620): nota DP
 * hidup yang sudah tersentuh uang BERSIH, menunggu ACC, atau punya laporan
 * transfer yang belum diputus. Fungsi SQL-nya tak bisa dipanggil klien
 * (EXECUTE dicabut), jadi syaratnya ditulis ulang di sini — nota DP `open`
 * yang masih perawan TIDAK mengunci (resync DP memang menyusulkan nominalnya).
 */
export function productionDpInvoiceLocked(
  rows: readonly ProductionDpInvoiceRow[]
): boolean {
  return rows.some(
    (r) =>
      ["pending_acc", "open", "partial", "paid"].includes(r.status) &&
      (r.amountPaid + r.balanceApplied - r.refundedAmount > 0 ||
        r.status === "pending_acc" ||
        r.hasPendingPayment)
  );
}

/**
 * Alasan pesanan SATU pembeli tak bisa diedit dari Detail Produksi — null =
 * boleh. Cermin gembok tingkat pesanan RPC `admin_edit_production_order`
 * (mig 7320), urutan & kalimatnya sama supaya layar tak menjanjikan Simpan
 * yang pasti ditolak:
 *   1. lebih dari satu pesanan aktif (RPC menulis SATU order; layar ini tak
 *      menggabungkan — itu urusan keranjang admin),
 *   2. order bukan 'Produksi' / tak ada baris produksi,
 *   3. nota aktif / pcs terbayar, 4. nota DP tersentuh pembayaran,
 *   5. harga manual (mig 7060), 6. voucher (mig 5610).
 */
export function productionOrderEditLockedReason(input: {
  /** Jumlah pesanan non-Batal pembeli ini untuk produk ini. */
  orderCount: number;
  orderStatus: string | null;
  rows: readonly ProductionEditItemRow[];
  /** Hasil `order_dp_invoice_locked` versi klien (nota DP tersentuh uang). */
  dpLocked: boolean;
}): string | null {
  if (input.orderCount > 1) {
    return "Pelanggan ini punya lebih dari satu pesanan aktif untuk produk ini — rapikan lewat keranjang admin.";
  }
  if (input.orderStatus !== "Produksi") {
    return `Pesanan tidak bisa diedit pada status ${input.orderStatus ?? "—"}.`;
  }
  const editable = input.rows.filter(isProductionEditableRow);
  if (editable.length === 0) {
    return "Tidak ada item produksi yang bisa diedit di pesanan ini.";
  }
  if (editable.some((r) => r.paidQty > 0 || r.inActiveInvoice)) {
    return "Pesanan sudah masuk tagihan / ada pcs terbayar — selesaikan atau void tagihannya dulu sebelum edit.";
  }
  if (input.dpLocked) {
    return "Pesanan sudah masuk tagihan / ada pcs terbayar — tagihan DP-nya sudah tersentuh pembayaran, selesaikan atau batalkan tagihannya dulu sebelum edit.";
  }
  if (
    editable.some(
      (r) => r.manualPriceNormal != null || r.manualDiscountMode != null
    )
  ) {
    return "Ada harga manual di pesanan ini — Edit Pesanan tidak tersedia. Kembalikan ke harga otomatis dulu (Ubah Harga / Ubah Diskon di keranjang), atau pakai Ubah Pcs / Batalkan item.";
  }
  if (
    input.rows.some(
      (r) => r.voucherUsageId != null && r.keepStatus !== "cancelled"
    )
  ) {
    return "Pesanan ini memakai voucher — Edit Pesanan tidak tersedia. Ubah lewat Ubah Pcs / Batalkan item.";
  }
  return null;
}

/** Qty default modal Tandai Ready (fase A: pcs yang disubmit; fase B: sisa target). */
export function defaultReadyQty(
  v: Pick<
    ProductionDetailVariant,
    "qtyActive" | "qtyOrdered" | "qtyArrived" | "qtySubmitted"
  >,
  isPhaseA: boolean
): number {
  // Fase A: targetnya BELUM ada di product_variants.qty_ordered — RPC
  // mark_variants_ready menjalankan tandai_mulai_dikirim dulu, yang mengisinya
  // dari pcs ber-`item_status='Produksi'` SAJA. Memakai qtyActive di sini
  // membuat batas input lebih longgar dari yang akan diterima RPC: admin boleh
  // mengetik angka yang lalu dipangkas diam-diam, dan pratinjau FIFO menyebut
  // nama pembeli yang tak akan menerima barang (bughunt C3-5).
  // qtySubmitted null = kolom item_status tak diminta → jatuh balik ke qtyActive.
  if (isPhaseA) return v.qtySubmitted ?? v.qtyActive;
  return Math.max(0, v.qtyOrdered - v.qtyArrived);
}

/**
 * Status satu item pesanan (bukan agregat varian) untuk rekap per-reseller:
 * - `gagal`   — item gagal produksi (cancelled; hanya item gagal_produksi yang
 *               masuk daftar, cancel lain di-skip sebelum fungsi ini dipanggil).
 * - `dikirim` — sudah requested_shipment/shipped.
 * - `ready`   — sudah di Keranjang Ready (unpaid_keep/paid_keep/hold).
 * - `belum`   — masih dalam proses.
 */
export function deriveProductionItemStatus(item: {
  keep_status: string | null;
  cancel_reason: string | null;
  ready_at?: string | null;
}): ProductionItemStatus {
  if (item.keep_status === "cancelled") return "gagal";
  // mig 7760: baris paket yang barangnya belum datang masih "belum" bagi
  // produksi — ia menunggu barang yang sama dengan baris Keranjang.
  if (isWaitingPackageRow(item)) return "belum";
  if (item.keep_status && SHIPPED_KEEPS.has(item.keep_status)) return "dikirim";
  if (item.keep_status && TAGGED_KEEPS.has(item.keep_status)) return "ready";
  return "belum";
}

/**
 * Produk ini berada di KOLOM READY board (`/admin/orders` → Produksi → Ready,
 * dan kembarannya Selesai): satu-satunya keadaan di mana mengabari pembeli
 * "barangnya sudah ready" itu benar.
 *
 * Sengaja diturunkan dari `status` saja, bukan dari `production_completed_at`:
 * kolom Selesai (mig 3450) memuat produk 'READY LENGKAP' yang SAMA — ia cuma
 * penanda kerapian board, bukan fase lifecycle, dan pembeli yang belum sempat
 * dikabari saat kartunya dituntaskan tetap harus bisa dikabari.
 */
export function isProductionReadyColumn(
  data: Pick<ProductionDetailData, "status">
): boolean {
  return data.status === "READY LENGKAP";
}

/**
 * Produk masih di KOLOM PRODUKSI board (belum ditandai ready) — satu-satunya
 * fase tombol Rekap & PDF per pelanggan dirender di Detail Produksi
 * (permintaan pemilik 2026-09-29: "hanya saat di produksi, ketika sudah
 * ditandai ready tidak muncul"). Kolom Ready punya tombolnya sendiri (Info
 * Ready, `isProductionReadyColumn`), jadi keduanya tak pernah tampil bersamaan;
 * produk GAGAL tak mendapat keduanya.
 */
export function isProductionPhase(
  data: Pick<ProductionDetailData, "status">
): boolean {
  return data.status === "PROSES PRODUKSI";
}

/**
 * Tombol "Cetak Tagihan" & "Buat Tagihan" massal (mig 6980) dirender di tab
 * Rekap Per Pelanggan: kolom Produksi DAN kolom Ready (putusan pemilik
 * 2026-10-07) — barang Ready yang belum ditagih tetap harus bisa ditagih &
 * dicetak. Produk GAGAL / status lain tidak.
 */
export function productionInvoiceActionsAvailable(
  data: Pick<ProductionDetailData, "status">
): boolean {
  return isProductionPhase(data) || isProductionReadyColumn(data);
}

/**
 * Tombol Rekap & PDF dirender untuk pembeli ini: produk di kolom Produksi DAN
 * pembelinya masih punya pcs aktif. Kartu yang SELURUH pcs-nya gagal produksi
 * tetap tampil (badge "gagal N pcs"), tapi rekapnya akan berbunyi "Total
 * Produk = 0 pcs" / PDF "Belum ada pesanan" — audit 2026-09-29, 5 kartu PROD.
 */
export function productionRecapAvailable(
  data: Pick<ProductionDetailData, "status">,
  r: Pick<ProductionDetailReseller, "totalQty">
): boolean {
  return isProductionPhase(data) && r.totalQty > 0;
}

/**
 * Pembeli dalam bentuk yang dibaca pesan Rekap & PDF (`poRecap.ts`): item
 * AKTIF saja. `items` kartu produksi ikut memuat baris gagal produksi (badge
 * merah, di luar `totalQty`); tanpa saringan ini pesan tanpa harga dan catatan
 * PDF akan mencetak pcs yang sudah dibatalkan.
 */
export function productionRecapView(
  r: ProductionDetailReseller
): ProductionDetailReseller {
  return { ...r, items: r.items.filter((it) => it.status !== "gagal") };
}

/**
 * Kalimat penanda "sudah dikabari ready" pada kartu Rekap Per Reseller
 * (mig 4670) — mis. "Diinfokan 2 jam lalu · Azra".
 *
 * Cermin `poConfirmedLabel`: umurnya RELATIF karena yang ditanyakan admin di
 * tengah menyisir daftar adalah "barusan atau kemarin?", bukan jam persisnya.
 * Nama penanda OPSIONAL — profil yang dihapus membuat `readyNotifiedByName`
 * null (FK SET NULL), dan itu tak boleh menghapus fakta bahwa kabarnya pernah
 * terkirim.
 */
export function readyNotifiedLabel(
  notifiedAt: string | null,
  notifiedByName?: string | null,
  now: Date = new Date()
): string | null {
  if (!notifiedAt) return null;
  if (Number.isNaN(Date.parse(notifiedAt))) return null;
  const who = (notifiedByName ?? "").trim();
  const age = formatNotifAge(notifiedAt, now);
  return who ? `Diinfokan ${age} · ${who}` : `Diinfokan ${age}`;
}

/** Filter daftar reseller: cari nama atau nomor HP (case-insensitive). */
export function filterProductionResellers(
  resellers: ProductionDetailReseller[],
  search: string
): ProductionDetailReseller[] {
  const q = search.trim().toLowerCase();
  if (!q) return resellers;
  return resellers.filter(
    (r) =>
      r.customerName.toLowerCase().includes(q) ||
      r.phone.toLowerCase().includes(q)
  );
}

/** Filter daftar varian: cari nama (case-insensitive) + chip status. */
export function filterProductionVariants(
  variants: ProductionDetailVariant[],
  search: string,
  statusFilter: ProductionVariantStatus | "all"
): ProductionDetailVariant[] {
  const q = search.trim().toLowerCase();
  return variants.filter(
    (v) =>
      (statusFilter === "all" || v.status === statusFilter) &&
      (!q || v.variantName.toLowerCase().includes(q))
  );
}

/** Satu baris varian di dalam grup Variasi 1 (axis pertama nama varian). */
export type ProductionVariantRow = {
  variant: ProductionDetailVariant;
  /**
   * Label sisa axis (axis ke-2 dst), mis. "S" atau "S / Katun". Kosong ("")
   * bila varian hanya satu-axis (nama tanpa pemisah " / ") — UI fallback ke
   * nama varian penuh saat render.
   */
  subLabel: string;
};

/** Grup rekap varian berdasar nilai Variasi 1 (axis pertama nama varian). */
export type ProductionVariantGroup = {
  /**
   * Nilai axis pertama, mis. "Merah". Kosong ("") menandai MODE FLAT (produk
   * satu-axis): UI render daftar datar tanpa header grup dan memakai
   * `row.subLabel` (= nama varian penuh) sebagai label tiap baris.
   */
  axisLabel: string;
  /** Σ qtyActive seluruh baris dalam grup. */
  totalQty: number;
  rows: ProductionVariantRow[];
};

/**
 * Kelompokkan rekap per-varian Detail Produksi berdasar **Variasi 1** (axis
 * pertama nama varian; nama dipisah " / ") agar tampilannya terkelompok &
 * mudah dibaca seperti halaman Detail Pre-order (`groupPODetailVariants` di
 * board.ts). Logikanya cermin: multi-axis → satu grup per nilai axis-1 (baris
 * pakai sisa axis lewat `subLabel`); satu-axis → SATU grup `axisLabel` ""
 * (mode flat) berisi semua varian dengan `subLabel` = nama penuh.
 *
 * Urutan grup mengikuti kemunculan pertama axis-1 (input sudah terurut
 * sort_order dari `aggregateProductionDetail`); urutan baris = urutan input.
 */
export function groupProductionVariants(
  variants: ProductionDetailVariant[]
): ProductionVariantGroup[] {
  if (variants.length === 0) return [];

  const multiAxis = variants.some((v) => v.variantName.includes(" / "));
  if (!multiAxis) {
    return [
      {
        axisLabel: "",
        totalQty: variants.reduce((sum, v) => sum + v.qtyActive, 0),
        rows: variants.map((v) => ({ variant: v, subLabel: v.variantName }))
      }
    ];
  }

  const order: string[] = [];
  const rowsByAxis = new Map<string, ProductionVariantRow[]>();
  for (const v of variants) {
    const parts = v.variantName
      .split(" / ")
      .map((p) => p.trim())
      .filter(Boolean);
    const axis = parts[0] ?? v.variantName;
    const subLabel = parts.slice(1).join(" / ");
    if (!rowsByAxis.has(axis)) {
      rowsByAxis.set(axis, []);
      order.push(axis);
    }
    rowsByAxis.get(axis)!.push({ variant: v, subLabel });
  }

  return order.map((axis) => {
    const rows = rowsByAxis.get(axis)!;
    return {
      axisLabel: axis,
      totalQty: rows.reduce((sum, r) => sum + r.variant.qtyActive, 0),
      rows
    };
  });
}

/**
 * Agregasi murni (tanpa I/O) halaman Detail Produksi: dari header produk +
 * daftar order, hasilkan rekap per-varian (qty aktif/gagal, progress
 * kedatangan, keep, catatan) + ringkasan. Item diagregasi BY variant_name
 * (idiom aggregatePODetail) — item bisa pecah-baris, jadi jumlahkan qty,
 * jangan hitung baris.
 */
export function aggregateProductionDetail(
  header: ProductionHeaderRow,
  orders: ProductionOrderRow[]
): ProductionDetailData {
  type VariantMeta = {
    id: string;
    sku: string | null;
    sortOrder: number;
    qtyOrdered: number;
    qtyArrived: number;
    arrivalStatus: ProductionArrivalStatus;
    productionStatus: ProductionDetailVariant["productionStatus"];
    isActive: boolean;
  };
  const metaByName = new Map<string, VariantMeta>();
  // Dua peta HPP: `hppByName` (nol-untuk-null) menjumlah estimasi totalHpp
  // seperti sebelumnya, `rawHppByName` menjaga bedanya null vs 0 supaya modal
  // Hitung HPP tak salah mengira varian tanpa izin baca sebagai ber-HPP nol.
  const hppByName = new Map<string, number>();
  const rawHppByName = new Map<string, number | null>();
  const priceByName = new Map<string, number | null>();
  // Tambahan per varian (mig 7900); absen di daftar select = 0.
  const extraByName = new Map<string, number>();
  // Rentang harga SELURUH baris varian, termasuk yang tak dipesan — lihat
  // `ProductionDetailData.variantPriceRange`.
  let priceMin: number | null = null;
  let priceMax: number | null = null;
  // Rentang TAMBAHAN baris berharga — uji kesepakatan pinjaman (bughunt D14-2).
  let extraMin: number | null = null;
  let extraMax: number | null = null;
  for (const v of header.product_variants ?? []) {
    hppByName.set(v.name, v.hpp ?? 0);
    rawHppByName.set(v.name, v.hpp ?? null);
    priceByName.set(v.name, v.price_normal ?? null);
    const extraNum = v.price_extra == null || v.price_extra === "" ? 0 : Number(v.price_extra);
    extraByName.set(v.name, Number.isFinite(extraNum) && extraNum > 0 ? extraNum : 0);
    // Rentang atas harga UTUH (dasar + tambahan) — lihat `variantOwnTotal`.
    const price = variantOwnTotal(v.price_normal, v.price_extra) ?? NaN;
    if (Number.isFinite(price)) {
      if (priceMin === null || price < priceMin) priceMin = price;
      if (priceMax === null || price > priceMax) priceMax = price;
      const extra = extraByName.get(v.name) ?? 0;
      if (extraMin === null || extra < extraMin) extraMin = extra;
      if (extraMax === null || extra > extraMax) extraMax = extra;
    }
    metaByName.set(v.name, {
      id: v.id,
      sku: v.sku?.trim() ? v.sku.trim() : null,
      sortOrder: v.sort_order ?? Number.MAX_SAFE_INTEGER,
      qtyOrdered: v.qty_ordered ?? 0,
      qtyArrived: v.qty_arrived ?? 0,
      arrivalStatus: (v.arrival_status as ProductionArrivalStatus) ?? "pending",
      productionStatus:
        (v.production_status as ProductionDetailVariant["productionStatus"]) ?? null,
      isActive: v.is_active !== false
    });
  }

  type VariantAgg = {
    variantName: string;
    qtyActive: number;
    qtyFailed: number;
    qtyReadyTagged: number;
    qtyShipped: number;
    qtySubmitted: number;
    qtyBatch: number;
    /** Ada baris yang benar-benar membawa `item_status` → qtySubmitted/qtyBatch bermakna. */
    hasItemStatus: boolean;
    // Pembeli (aktif) per varian, keyed by customer_id (fallback nama) → qty
    // dijumlah (cermin aggregatePODetail) untuk rekap "N pembeli" per varian.
    buyerMap: Map<string, ProductionVariantBuyer>;
    notes: VariantNoteEntry[];
  };
  const aggByName = new Map<string, VariantAgg>();
  const getAgg = (name: string): VariantAgg => {
    let agg = aggByName.get(name);
    if (!agg) {
      agg = {
        variantName: name,
        qtyActive: 0,
        qtyFailed: 0,
        qtyReadyTagged: 0,
        qtyShipped: 0,
        qtySubmitted: 0,
        qtyBatch: 0,
        hasItemStatus: false,
        buyerMap: new Map(),
        notes: []
      };
      aggByName.set(name, agg);
    }
    return agg;
  };

  // Rekap per-reseller: dibuat lazy (hanya saat ada item tampil) supaya order
  // yang seluruh itemnya di-skip tak memunculkan kartu kosong. Key = customer_id
  // (fallback nama) agar beberapa order satu pelanggan tergabung.
  const resellerMap = new Map<string, ProductionDetailReseller>();
  const resellerKeyOf = (order: ProductionOrderRow) =>
    order.customer_id ?? `name:${order.customer_name ?? "—"}`;
  // Indeks baris uang lintas pembeli — kunci lihat `addOrderItemPriceLine`.
  const priceLineIndex = new Map<string, PODetailPriceLine>();
  const getReseller = (order: ProductionOrderRow): ProductionDetailReseller => {
    const key = resellerKeyOf(order);
    let r = resellerMap.get(key);
    if (!r) {
      const custRel = Array.isArray(order.customers)
        ? order.customers[0]
        : order.customers;
      r = {
        customerId: order.customer_id ?? null,
        customerName: order.customer_name ?? "—",
        phone: order.phone ?? "",
        handlerName: custRel?.profiles?.full_name ?? null,
        totalQty: 0,
        failedQty: 0,
        readyNotifiedAt: null,
        readyNotifiedByName: null,
        items: [],
        groupId: custRel?.group_id ?? null,
        priceLines: []
      };
      resellerMap.set(key, r);
    }

    // Stempel kabar ready (mig 4670): ambil yang TERBARU lintas order milik
    // pembeli ini. Dibandingkan lewat `Date.parse`, bukan perbandingan string —
    // stempel dari dua sumber bisa beda offset zona waktu ("+00:00" vs "Z"),
    // dan urutan leksikalnya tidak lagi mencerminkan urutan waktunya (cermin
    // `aggregatePODetail` untuk penanda konfirmasi PO).
    //
    // Diperbarui di SETIAP pemanggilan, bukan hanya saat kartunya baru dibuat:
    // kartu dibuat oleh order pertama yang punya item tampil, sedangkan
    // stempel terbaru bisa ada di order berikutnya milik pembeli yang sama.
    const stamp = order.ready_notified_at ?? null;
    if (stamp) {
      const prev = r.readyNotifiedAt ? Date.parse(r.readyNotifiedAt) : NaN;
      const next = Date.parse(stamp);
      if (!Number.isNaN(next) && (Number.isNaN(prev) || next > prev)) {
        const rel = Array.isArray(order.ready_notified_profile)
          ? order.ready_notified_profile[0]
          : order.ready_notified_profile;
        r.readyNotifiedAt = stamp;
        r.readyNotifiedByName = rel?.full_name ?? null;
      }
    }

    return r;
  };

  // Kandidat Belum Ready (keep_status NULL) per nama varian → pratinjau FIFO
  // di modal Tandai Ready. Disusun urut waktu order di akhir.
  const candidatesByName = new Map<string, ReadyCandidate[]>();

  let hasProduksiOrder = false;
  let hasBelumReadyOrder = false;
  for (const order of orders ?? []) {
    if (order.status === "Produksi") hasProduksiOrder = true;
    if (order.status === "Belum Ready") hasBelumReadyOrder = true;
    const buyerName = order.customer_name ?? "—";
    for (const item of order.order_items ?? []) {
      const qty = item.qty ?? 0;
      const note = (item.item_note ?? "").trim();
      if (item.keep_status === "cancelled") {
        // Hanya gagal produksi yang tetap tampil (badge merah); cancel lain skip.
        if (item.cancel_reason === "gagal_produksi") {
          getAgg(item.variant_name).qtyFailed += qty;
          const reseller = getReseller(order);
          reseller.failedQty += qty;
          reseller.items.push({
            variantName: item.variant_name,
            qty,
            note: note || null,
            status: "gagal"
          });
        }
        continue;
      }
      const agg = getAgg(item.variant_name);
      agg.qtyActive += qty;
      // Pcs yang benar-benar masuk batch produksi = dasar target fase A
      // (cermin tandai_mulai_dikirim §(b), mig 2220).
      if (item.item_status != null) {
        agg.hasItemStatus = true;
        if (item.item_status === SUBMITTED_ITEM_STATUS) agg.qtySubmitted += qty;
        // Peserta batch = semua yang BUKAN 'Menunggu' (mig 5110). Mengecualikan
        // satu status, bukan mendaftar yang boleh, supaya status yang lahir
        // kemudian tetap terhitung — cermin persis predikat RPC-nya.
        if (item.item_status !== WAITING_ITEM_STATUS) agg.qtyBatch += qty;
      }
      // Status item satu sumber dengan badge kartu Rekap Per Pelanggan.
      const itemStatus = deriveProductionItemStatus(item);
      // Pembeli per varian (aktif) → rekap "N pembeli" + qty per pembeli,
      // plus pcs ready/dikirim-nya (pecah-baris ready sebagian dijumlah).
      const buyerKey = order.customer_id ?? `name:${buyerName}`;
      let buyer = agg.buyerMap.get(buyerKey);
      if (!buyer) {
        buyer = { buyerName, qty: 0, readyQty: 0, shippedQty: 0 };
        agg.buyerMap.set(buyerKey, buyer);
      }
      buyer.qty += qty;
      if (itemStatus === "ready" || itemStatus === "dikirim") buyer.readyQty += qty;
      if (itemStatus === "dikirim") buyer.shippedQty += qty;
      // Kandidat penerima stok datang (FIFO) — syaratnya di satu predikat
      // bersama mesin SQL (mig 4750).
      if (isReadyCandidate(item, order)) {
        let list = candidatesByName.get(item.variant_name);
        if (!list) {
          list = [];
          candidatesByName.set(item.variant_name, list);
        }
        list.push({
          itemId: item.id,
          qty,
          buyerName,
          orderCreatedAt: order.created_at ?? "",
          itemCreatedAt: item.created_at ?? undefined
        });
      }
      if (item.keep_status && TAGGED_KEEPS.has(item.keep_status)) {
        agg.qtyReadyTagged += qty;
      }
      // mig 7760: baris paket yang menunggu barang belum menyerap kedatangan —
      // cermin `v_consumed` mesin SQL; ia sudah masuk `readyCandidates` di atas.
      if (
        item.keep_status &&
        SHIPPED_KEEPS.has(item.keep_status) &&
        !isWaitingPackageRow(item)
      ) {
        agg.qtyShipped += qty;
      }
      if (note) agg.notes.push({ buyerName, note });

      const reseller = getReseller(order);
      reseller.totalQty += qty;
      reseller.items.push({
        variantName: item.variant_name,
        qty,
        note: note || null,
        status: itemStatus
      });
      // Baris uang item AKTIF — aturan satu dengan Detail PO.
      addOrderItemPriceLine(
        reseller.priceLines,
        priceLineIndex,
        resellerKeyOf(order),
        item,
        qty
      );
    }
  }

  // Varian aktif ber-target tapi tanpa item (defensif) tetap ditampilkan.
  for (const [name, meta] of metaByName) {
    if (meta.isActive && meta.qtyOrdered > 0 && !aggByName.has(name)) {
      getAgg(name);
    }
  }

  const variants: ProductionDetailVariant[] = [...aggByName.values()]
    .map((agg) => {
      const meta = metaByName.get(agg.variantName);
      const base = {
        variantId: meta?.id ?? null,
        variantName: agg.variantName,
        sku: meta?.sku ?? null,
        priceNormal: priceByName.get(agg.variantName) ?? null,
        priceExtra: extraByName.get(agg.variantName) ?? 0,
        hpp: rawHppByName.get(agg.variantName) ?? null,
        qtyActive: agg.qtyActive,
        qtyFailed: agg.qtyFailed,
        qtySubmitted: agg.hasItemStatus ? agg.qtySubmitted : null,
        qtyBatch: agg.hasItemStatus ? agg.qtyBatch : null,
        qtyOrdered: meta?.qtyOrdered ?? 0,
        qtyArrived: meta?.qtyArrived ?? 0,
        arrivalStatus: meta?.arrivalStatus ?? ("pending" as ProductionArrivalStatus),
        qtyReadyTagged: agg.qtyReadyTagged,
        qtyShipped: agg.qtyShipped,
        hasShippedPcs: agg.qtyShipped > 0,
        productionStatus: meta?.productionStatus ?? null,
        resellerCount: agg.buyerMap.size,
        buyers: [...agg.buyerMap.values()].sort((a, b) =>
          a.buyerName.localeCompare(b.buyerName, "id")
        ),
        notes: agg.notes,
        readyCandidates: sortReadyCandidates(
          candidatesByName.get(agg.variantName) ?? []
        ),
        sortOrder: meta?.sortOrder ?? Number.MAX_SAFE_INTEGER
      };
      return { ...base, status: deriveVariantStatus(base) };
    })
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        smartVariantNameCompare(a.variantName, b.variantName)
    )
    .map(({ sortOrder: _sortOrder, ...v }) => v);

  const hasTarget = [...metaByName.values()].some(
    (m) => m.isActive && m.qtyOrdered > 0
  );

  const resellers: ProductionDetailReseller[] = [...resellerMap.values()]
    .sort((a, b) => a.customerName.localeCompare(b.customerName, "id"))
    .map((r) => ({
      ...r,
      items: [...r.items].sort((a, b) =>
        smartVariantNameCompare(a.variantName, b.variantName)
      ),
      priceLines: sortPriceLines(r.priceLines)
    }));

  const productPriceNormal =
    header.price_normal != null ? Number(header.price_normal) : null;
  const variantPriceRange: VariantPriceSummary | null =
    priceMin !== null && priceMax !== null
      ? { min: priceMin, max: priceMax, extraMin: extraMin ?? 0, extraMax: extraMax ?? 0 }
      : null;
  // Kolom tak dimuat (route ekspor yang merakit header sendiri) = tak diketahui.
  const priceAxis =
    header.price_affecting_groups === undefined
      ? null
      : hasPriceAxis(header.price_affecting_groups);

  return {
    productId: header.id,
    productName: header.name,
    brand: header.brand ?? "",
    status: header.status,
    isPhaseA: !hasTarget && hasProduksiOrder,
    hasBelumReadyOrder,
    hasProductionTarget: hasTarget,
    totalActivePcs: variants.reduce((s, v) => s + v.qtyActive, 0),
    totalOrdered: variants.reduce((s, v) => s + v.qtyOrdered, 0),
    totalArrived: variants.reduce((s, v) => s + v.qtyArrived, 0),
    totalFailedPcs: variants.reduce((s, v) => s + v.qtyFailed, 0),
    totalReadyTagged: variants.reduce((s, v) => s + v.qtyReadyTagged, 0),
    totalShipped: variants.reduce((s, v) => s + v.qtyShipped, 0),
    readyVariantCount: variants.filter((v) => v.status === "ready").length,
    totalVariantCount: variants.length,
    totalHpp: variants.reduce(
      (s, v) => s + (hppByName.get(v.variantName) ?? 0) * v.qtyActive,
      0
    ),
    targetCost:
      header.production_target_cost != null
        ? Number(header.production_target_cost)
        : null,
    productPriceNormal,
    variantPriceRange,
    costModeOverride:
      header.production_cost_mode === "supplier" ||
      header.production_cost_mode === "produksi"
        ? header.production_cost_mode
        : null,
    hppMode: header.hpp_mode ?? null,
    priceAxis,
    needsPrice: productNeedsPrice(variants, productPriceNormal, variantPriceRange, priceAxis),
    estimatedReady: header.estimated_ready ?? null,
    variantPricing: (header.product_variants ?? []).map(variantPricingFromRow),
    variants,
    resellers
  };
}
